import { Trans } from "@lingui/macro";
import { IconButton, useMediaQuery, useTheme } from "@mui/material";
import { useIsomorphicLayoutEffect } from "@react-hookz/web";
import { UiBox, UiDialogResponsive, UiTypography } from "@smp/ui";
import { dataArrayShuffle } from "@smp/utils";
import { IconX } from "@tabler/icons-react";
import clsx from "clsx";
import { FC, useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useContextPolotnoTemplates } from "@/components/features/polotno_templates/context";
import {
  useTemplateMeta,
  useTemplateVirtualizer,
} from "@/components/features/polotno_templates/hooks";
import { PageNotFound } from "@/components/feedback/pages/page_not_found";
import { HelperAdapterLink } from "@/components/helpers/helper_adapter_link";
import { useQueryPolotnoTemplate } from "@/queries/polotno/use_query_polotno_template";
import { useQueryPolotnoTemplates } from "@/queries/polotno/use_query_polotno_templates";
import { customGeneratePath } from "@/utils";

import { TemplateCard } from "../template_card";
import classes from "./template_dialog.module.css";
import { TemplateHelmet } from "./template_helmet";
import { TemplateInfo } from "./template_info";
import { TemplatePreview } from "./template_preview";

export const TemplateDialog: FC<{
  enablePortal?: boolean;
}> = ({ enablePortal }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const {
    state: { pathPattern, activeTemplateId, isRoutingDisabled },
    actions: { setActiveEntity },
  } = useContextPolotnoTemplates();

  const parentRef = useRef<HTMLDivElement | null>(null);
  const [headerRef, setHeaderRef] = useState<HTMLDivElement | null>(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  const backUrl = useMemo(() => customGeneratePath(pathPattern), [pathPattern]);

  const { data: chosenTemplate, isLoading: isTemplateLoading } =
    useQueryPolotnoTemplate({
      id: activeTemplateId,
    });

  const meta = useTemplateMeta(chosenTemplate);

  const similarTemplatesParams = {
    tags__slug__in: chosenTemplate?.tags.filter(
      (tag) => !tag.startsWith("author_"),
    ),
    page_size: 100,
    ordering: "template_name",
  };

  const { data: similarTemplatesData, isLoading: isSimilarTemplatesLoading } =
    useQueryPolotnoTemplates(similarTemplatesParams, {
      enabled: !!chosenTemplate?.tags.length,
    });

  const similarTemplates = useMemo(
    () =>
      dataArrayShuffle(
        similarTemplatesData?.pages
          .flatMap(({ results }) => results)
          .filter((item) => item.id !== chosenTemplate?.id)
          .map(
            (item) =>
              ({
                key: `template-${item.id}`,
                payload: item,
                type: "template",
              } as const),
          ) || [],
      ),
    [chosenTemplate?.id, similarTemplatesData?.pages],
  );

  const { virtualizer, calculateStyles, getTotalSizeCustom } =
    useTemplateVirtualizer(similarTemplates, parentRef.current, {
      scrollMargin,
      scrollToFn: (offset) => offset,
    });

  const virtualItems = virtualizer.getVirtualItems();

  const handleClose = useCallback(() => {
    setActiveEntity(undefined);

    if (!isRoutingDisabled) {
      navigate(customGeneratePath(pathPattern), {
        replace: true,
      });
    }
  }, [isRoutingDisabled, navigate, pathPattern, setActiveEntity]);

  useIsomorphicLayoutEffect(() => {
    if (headerRef) {
      setScrollMargin(headerRef?.clientHeight);

      const resizeObserver = new ResizeObserver(() => {
        setScrollMargin(headerRef?.clientHeight || 0);
      });

      resizeObserver.observe(headerRef);
    }
  }, [headerRef]);

  return (
    <UiDialogResponsive
      open={Boolean(activeTemplateId)}
      fullWidth
      disablePortal={enablePortal !== true}
      isButtonCloseHidden
      onClose={handleClose}
      className={classes.dialog}
      classes={{
        dialogContent: classes.dialog__content,
        dialogTitle: classes.dialog__title,
        paper: clsx(
          classes.dialog__paper,
          !chosenTemplate &&
            activeTemplateId !== undefined &&
            classes.dialog__paper_404,
        ),
      }}
      isLoading={isTemplateLoading || isSimilarTemplatesLoading}
    >
      <IconButton
        onClick={handleClose}
        className={classes.dialog__closeBtn}
        component={!isRoutingDisabled ? HelperAdapterLink : "button"}
        to={!isRoutingDisabled ? backUrl : undefined}
      >
        <IconX />
      </IconButton>

      {!chosenTemplate && activeTemplateId !== undefined ? (
        <PageNotFound />
      ) : (
        <>
          {meta && <TemplateHelmet meta={meta} />}

          <div className={classes.container} ref={parentRef}>
            <div
              className={clsx(
                classes.scroller,
                import.meta.env.SSR && classes.scroller_ssr,
              )}
              style={{
                height:
                  !import.meta.env.SSR && virtualItems.length
                    ? `${getTotalSizeCustom()}px`
                    : undefined,
              }}
            >
              <div className={classes.scroller__header} ref={setHeaderRef}>
                <UiBox
                  display="flex"
                  mb={6}
                  flexDirection={isMobile ? "column" : "row"}
                  columnGap={6}
                  rowGap={2}
                >
                  <TemplatePreview
                    src={chosenTemplate?.preview_url}
                    meta={meta}
                  />
                  {chosenTemplate && (
                    <TemplateInfo template={chosenTemplate} backUrl={backUrl} />
                  )}
                </UiBox>

                {!!virtualItems.length && (
                  <UiBox pb={3}>
                    <UiTypography variant="h5">
                      <Trans>You might also like</Trans>
                    </UiTypography>
                  </UiBox>
                )}
              </div>

              {(parentRef.current || import.meta.env.SSR) &&
                virtualItems.map((virtualItem) => {
                  const item = similarTemplates[virtualItem.index];

                  if (item.type !== "template") {
                    return null;
                  }

                  return (
                    <div
                      style={calculateStyles(virtualItem)}
                      key={virtualItem.key.toString()}
                    >
                      <div
                        ref={virtualizer.measureElement}
                        data-index={virtualItem.index}
                      >
                        <TemplateCard template={item.payload} />
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </>
      )}
    </UiDialogResponsive>
  );
};
